/**
 * Contracts for the org-level customer-managed encryption keys
 * (CMK / BYOK) surface shipped by migration 152. Lives in
 * `@rynora/contracts` so both the backend (`packages/api/src/routes/
 * org-encryption.ts`) and the frontend (`packages/landing-page/src/
 * features/dashboard/pages/OrgEncryptionPage.tsx`) import the same
 * Zod shapes — the public API surface is fully type-shared.
 *
 * See `docs/handbook/runbooks/customer-managed-keys.md` for the
 * end-to-end lifecycle narrative.
 */

import { z } from 'zod';

/**
 * Customer-supplied KEK provider. v1 ships `customer_aes256` only;
 * KMS adapters (`aws_kms`, `gcp_kms`, `azure_kv`) are tracked under
 * `pending-cmk-kms-providers`.
 */
export const OrgCmkProviderSchema = z.enum(['customer_aes256']);
export type OrgCmkProvider = z.infer<typeof OrgCmkProviderSchema>;

/**
 * How Rynora persists the customer KEK between API restarts. v1
 * ships `platform_wrapped` only; the honest-BYOK `in_memory_only`
 * mode (KEK wiped on restart, customer re-supplies via unlock
 * endpoint) is tracked under `pending-cmk-honest-byok-modes`.
 */
export const OrgCmkPersistenceSchema = z.enum(['platform_wrapped']);
export type OrgCmkPersistence = z.infer<typeof OrgCmkPersistenceSchema>;

/**
 * Lifecycle state of the org's CMK row. `active` is normal;
 * `rotating` means the worker is migrating in-scope rows from the
 * previous DEK to the new one; `disabled` means the customer has
 * started the 30-day grace window before crypto-shredding.
 */
export const OrgCmkStateSchema = z.enum(['active', 'rotating', 'disabled']);
export type OrgCmkState = z.infer<typeof OrgCmkStateSchema>;

/**
 * State read by the admin UI. Never includes the wrapped KEK or
 * wrapped DEK — those bytes are server-internal. The fingerprint is
 * the public verification handle; that is all the admin UI needs to
 * confirm "yes, this is my key".
 */
export const OrgCmkPublicStateSchema = z.object({
  enrolled: z.boolean(),
  state: OrgCmkStateSchema.nullable(),
  provider: OrgCmkProviderSchema.nullable(),
  persistence: OrgCmkPersistenceSchema.nullable(),
  kekFingerprint: z.string().nullable(),
  enrolledAt: z.string().datetime().nullable(),
  rotatedAt: z.string().datetime().nullable(),
  /**
   * If state === 'disabled', the timestamp the grace window started.
   * The UI uses this to render "X days until your key is shredded".
   */
  disableRequestedAt: z.string().datetime().nullable(),
  /**
   * Calendar-day count of the grace window. Today the server hard-
   * codes 30 but we surface it so the UI doesn't have to.
   */
  disableGraceDays: z.number().int().positive().nullable(),
  /**
   * Names of the tables that are covered by the org DEK when CMK is
   * active. The UI renders this as the "What's protected by your
   * key" list. v1 is a fixed allow-list defined server-side; future
   * versions may add per-org opt-in.
   */
  inScopeTables: z.array(z.string()),
  /**
   * Documented pre-requisites for enrolment. The admin UI uses this
   * to surface "you need to flip on MFA mandate / wire a SIEM
   * endpoint first" deep-links when the user is on the
   * not-yet-enrolled view.
   */
  prerequisites: z.object({
    mfaMandateEnabled: z.boolean(),
    siemEndpointConfigured: z.boolean(),
  }),
  /**
   * Convenience: are we using the BYOK path right now? Mirrors the
   * server's `orgHasActiveCmk(orgId)` helper.
   */
  cmkActiveForReads: z.boolean(),
});
export type OrgCmkPublicState = z.infer<typeof OrgCmkPublicStateSchema>;

/**
 * Enrolment body. The KEK is base64-encoded 32 random bytes (256
 * bits). We accept a label so an org running multiple environments
 * (staging / prod) can disambiguate the fingerprints surfaced on the
 * recovery sheet.
 */
export const OrgCmkEnrolBodySchema = z.object({
  kekBase64: z.string().min(44).max(120),
  /**
   * Optional human-readable label the admin types to remember which
   * KEK this row uses (e.g. "vault.acme.io/keys/rynora-prod-2026q2").
   * NOT used as a security primitive; stored for operational
   * convenience and surfaced on the recovery sheet.
   */
  label: z.string().trim().min(1).max(120).optional(),
  /**
   * Confirmation phrase the admin types into the form. We require
   * the exact ASCII string `ENROLL` (case-sensitive) to defend
   * against a misclick on a sensitive action.
   */
  confirmation: z.literal('ENROLL'),
});
export type OrgCmkEnrolBody = z.infer<typeof OrgCmkEnrolBodySchema>;

/**
 * Rotation body. The new KEK is required (rotation always uses a
 * fresh KEK in v1 — re-wrap-only rotation is tracked under
 * `pending-cmk-re-wrap-only-rotation`). Generating a new DEK is the
 * default and only mode in v1 too; the `reEncryptInScopeRows` flag
 * is preserved for forward-compat with the deferred "lazy migration
 * only" mode.
 */
export const OrgCmkRotateBodySchema = z.object({
  newKekBase64: z.string().min(44).max(120),
  newLabel: z.string().trim().min(1).max(120).optional(),
  confirmation: z.literal('ROTATE'),
  reEncryptInScopeRows: z.boolean().default(true),
});
export type OrgCmkRotateBody = z.infer<typeof OrgCmkRotateBodySchema>;

/**
 * Disable body. Sets `state = 'disabled'` and writes
 * `disable_requested_at = NOW()`. The grace-shred worker
 * crypto-shreds the wrapped DEK after `disable_grace_days` (default
 * 30). The admin can cancel the disable up until then.
 */
export const OrgCmkDisableBodySchema = z.object({
  /**
   * Free-text reason stored on the row for audit / support follow-
   * up. Not surfaced to anyone outside the org.
   */
  reason: z.string().trim().min(1).max(2000),
  /**
   * The admin must type the org slug to confirm. The route compares
   * case-insensitively against `organizations.slug`. A mismatch
   * returns `ORG_CMK_CONFIRMATION_MISMATCH`.
   */
  confirmationOrgSlug: z.string().trim().min(1).max(80),
});
export type OrgCmkDisableBody = z.infer<typeof OrgCmkDisableBodySchema>;

/**
 * Response from POST /encryption/enroll. The recovery sheet is a
 * UUID the admin uses to fetch the printable PDF at
 * `GET /encryption/recovery-sheet/:id` while it is still live
 * (10-minute TTL after enrol). The PDF reproduces the KEK
 * fingerprint, the list of in-scope tables, and the disable-grace
 * semantics so the admin has a physical record they enrolled the
 * right key.
 */
export const OrgCmkEnrolResponseSchema = z.object({
  state: OrgCmkPublicStateSchema,
  recoverySheetId: z.string().uuid(),
  recoverySheetExpiresAt: z.string().datetime(),
});
export type OrgCmkEnrolResponse = z.infer<typeof OrgCmkEnrolResponseSchema>;
